import { appPath } from '../lib/basePath';
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { i18nApi } from '../api/i18n';
import { profileApi } from '../api/profile';
import { fetchLibrary } from '../lib/libraryCache';
// 0.38.02 — Caches for the two sequential API calls this provider
// runs on mount. Without them, App.jsx line 308 gates the entire
// public app on this provider's `loading=true` for ~1.5s while
// getLanguages + getTranslations resolve.
import {
  getCachedLanguages, getCachedTranslations,
  fetchLanguages, fetchTranslations,
} from '../lib/i18nCache';

const LanguageContext = createContext(null);

const FALLBACK_LANG = 'hy';
const STORAGE_KEY = 'caf_lang';

export function LanguageProvider({ children }) {
  // 0.38.02 — Seed from cache. When both language list AND
  // translations for the initial language are cached, we can set
  // `loading=false` immediately and let the app render without
  // waiting for the network round-trips. The background refresh
  // fires anyway (in the useEffect below) and silently updates
  // state if anything changed.
  const _initialLangGuess = (typeof localStorage !== 'undefined'
    ? localStorage.getItem(STORAGE_KEY) : null) || FALLBACK_LANG;
  const _cachedLangs = getCachedLanguages();
  const _cachedTrans = getCachedTranslations(_initialLangGuess);

  // languages: [{code, name, is_default}, ...]
  const [languages, setLanguages] = useState(_cachedLangs || []);
  const [language, setLanguageState] = useState(_initialLangGuess);
  // translations: { [langCode]: { key: value } }
  const [translations, setTranslations] = useState(
    _cachedTrans ? { [_initialLangGuess]: _cachedTrans } : {}
  );
  // 0.38.03 — `loading` gates App.jsx's public-app render. Start
  // false as soon as we have the language list from cache — the
  // app is usable at that point, translations arrive silently.
  // Only start `loading=true` on truly-fresh visits (no cached
  // language list at all), where we must at least know what
  // language codes are enabled before we can render.
  const [loading, setLoading] = useState(!_cachedLangs);
  const [error, setError] = useState(null);

  // Bug 2 fix: tracks whether the language was set by an external path
  // (specifically setLanguageLocal called from the user-preferred-language
  // sync in App.jsx) while init() was still mid-flight awaiting translation
  // fetch. If true, init's final `setLanguageState(initialLang)` would
  // overwrite the better choice — so init skips that final commit.
  const externalLangAppliedRef = useRef(false);

  // Load the language list and the current language's strings on first mount
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const langs = await fetchLanguages();
        if (cancelled) return;
        setLanguages(langs);

        // Determine which language to load. If saved language is no longer enabled, fall back.
        const enabledCodes = langs.map(l => l.code);
        let initialLang = localStorage.getItem(STORAGE_KEY);
        if (!initialLang || !enabledCodes.includes(initialLang)) {
          const def = langs.find(l => l.is_default);
          initialLang = def ? def.code : (langs[0]?.code || FALLBACK_LANG);
        }

        // 0.38.03 — Release the loading gate here, BEFORE the
        // translations fetch. App.jsx line 308 gates the entire
        // public app on `loading` — waiting for translations to
        // arrive meant a fresh-visit user saw "Loading..." for
        // 5+ seconds (11k+ translation keys). Now: the app
        // renders as soon as we know the language list.
        //
        // UI chrome (header, buttons, nav) will briefly show
        // translation keys as fallback — t() returns the key
        // itself when translations aren't loaded — then flip
        // to real strings when the fetch resolves. Form content
        // (from pageCache) is unaffected.
        if (!externalLangAppliedRef.current) {
          setLanguageState(initialLang);
          localStorage.setItem(STORAGE_KEY, initialLang);
        }
        setLoading(false);

        // Translations fire AFTER the gate release. Silent
        // update when they arrive; re-render flips chrome from
        // keys to real strings.
        const strings = await fetchTranslations(initialLang);
        if (cancelled) return;
        setTranslations(prev => ({ ...prev, [initialLang]: strings }));
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load translations');
          setLoading(false);
        }
      }
    }
    init();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    // 0.44.72 - Month names for dates (Months and days library) in the
    // new language, loaded now rather than when the first date asks, so
    // a date does not show English names for a moment. The format
    // listeners re-render the dates when they arrive.
    if (language && !appPath().startsWith('/admin')) {
      fetchLibrary('months_and_days', language).catch(() => {});
    }
  }, [language]);

  // Allow callers to set the active UI language without persisting to the server.
  // Useful when we want to sync FROM the server-side preferred_language at login —
  // calling that path through the regular setLanguage would loop back to the server.
  const setLanguageLocal = useCallback(async (lang) => {
    const enabled = languages.some(l => l.code === lang);
    if (!enabled) return;
    if (!translations[lang]) {
      // 0.38.02 — Check the cache before hitting network. On repeat
      // language switches (or when the citizen already visited this
      // language before), the cache resolves synchronously via the
      // stale-while-revalidate path.
      const cachedTrans = getCachedTranslations(lang);
      if (cachedTrans) {
        setTranslations(prev => ({ ...prev, [lang]: cachedTrans }));
      }
      try {
        const strings = await fetchTranslations(lang);
        setTranslations(prev => ({ ...prev, [lang]: strings }));
      } catch (e) {
        console.error('Failed to load language', lang, e);
        if (!cachedTrans) return; // no fallback — abort switch
      }
    }
    // Mark that an external caller has chosen a language. If init() is still
    // running and about to commit its own choice, this flag tells it to skip.
    externalLangAppliedRef.current = true;
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, [languages, translations]);

  // setLanguage is the public API used by switchers throughout the app.
  // It updates the UI immediately AND tries to persist to the user's
  // preferred_language on the server (best-effort — failure is silent so a
  // logged-out visitor or a brief network hiccup doesn't block the UI flip).
  const setLanguage = useCallback(async (lang) => {
    await setLanguageLocal(lang);
    try {
      await profileApi.changeLanguage(lang);
    } catch {
      // Likely not signed in, or transient — UI change already applied,
      // so do nothing visible. Server-side preference simply stays old.
    }
  }, [setLanguageLocal]);

  const reloadLanguages = useCallback(async () => {
    try {
      const langs = await i18nApi.getLanguages();
      setLanguages(langs);
      // If current language was disabled or removed, fall back to default
      const enabledCodes = langs.map(l => l.code);
      if (!enabledCodes.includes(language)) {
        const def = langs.find(l => l.is_default) || langs[0];
        if (def) {
          const strings = await i18nApi.getTranslations(def.code);
          setTranslations({ [def.code]: strings });
          setLanguageState(def.code);
          localStorage.setItem(STORAGE_KEY, def.code);
        }
      }
    } catch (e) {
      console.error('Failed to reload languages', e);
    }
  }, [language]);

  const t = useCallback((key, vars) => {
    const current = translations[language];
    let value;
    if (current && current[key] !== undefined) {
      value = current[key];
    } else {
      // Fallback: try any loaded language, then return the key itself
      for (const lang of Object.keys(translations)) {
        if (translations[lang][key] !== undefined) {
          value = translations[lang][key];
          break;
        }
      }
    }
    if (value === undefined) return key;
    if (vars) {
      return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, name) =>
        Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : m
      );
    }
    return value;
  }, [language, translations]);

  return (
    <LanguageContext.Provider value={{
      language, setLanguage, setLanguageLocal, t,
      languages, loading, error,
      reloadLanguages,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
}

/* 0.44.63 - The language context, or null outside a LanguageProvider.
 * For shared components (the date picker) that translate when they can
 * and fall back to built-in strings when they cannot. */
export function useOptionalLanguage() {
  return useContext(LanguageContext);
}
